import { useCallback, useEffect, useRef, useState } from "react";

import {
  abortPatch,
  applyDelta,
  endPatch,
  startMessage,
  startTrack,
} from "../../brain/daNarrationLogic";

// A newer narration (same or new diagnosis) replaces a live one that never ended.
const ABORT_SUPERSEDED = "superseded";
// Client-side close (disconnect, clear): same rule as a server abort.
const ABORT_INTERRUPTED = "interrupted";
// Safety net for the "thinking" row between tse:da_narration_pending and the start. The server's narration
// deadline is 15 s (plus one regeneration before the template), so the row never times out before that.
export const NARRATION_WAIT_MAX_MS = 25000;

/**
 * useDaNarration — socket handlers for tse:da_narration_pending / _start / _delta / _end / _abort (V5 DA text).
 *
 * One live narration at a time (the contract drops events whose message_id is not the live bubble). The
 * bubble is an ordinary copilot message keyed by message_id, so CopilotPanel renders it like a hybrid answer:
 * SourceCards above (from start, before any text), StreamingMessage with [S<n>] pills, cursor while streaming.
 * The question card is NOT touched: it arrived first and stays docked; this bubble lands above it.
 *
 * `awaiting` drives the V4 "thinking" row from tse:da_narration_pending until the start opens the bubble (which then shows the same row inside itself until the first delta).
 *
 * A start for another diagnosis closes the previous live bubble (whole sentences kept). Replayed start/end
 * pairs for a bubble already painted from the da_narration replay block are ignored.
 */
export default function useDaNarration({ appendMessage, patchMessage, setMessages, hasMessage, enabled = true }) {
  const liveRef = useRef(null);
  const sourcesRef = useRef([]);
  const [awaiting, setAwaiting] = useState(false);
  const waitTimerRef = useRef(null);
  // Same safety net for a started bubble that gets no further event (re-armed by every delta).
  const liveTimerRef = useRef(null);
  const clearLiveTimer = () => {
    if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
    liveTimerRef.current = null;
  };

  const stopWaiting = useCallback(() => {
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
    waitTimerRef.current = null;
    setAwaiting(false);
  }, []);

  const startWaiting = useCallback(() => {
    if (!enabled) return;
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
    waitTimerRef.current = setTimeout(() => {
      waitTimerRef.current = null;
      setAwaiting(false);
    }, NARRATION_WAIT_MAX_MS);
    setAwaiting(true);
  }, [enabled]);

  useEffect(() => () => {
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
    clearLiveTimer();
  }, []);

  const closeLive = useCallback((reason) => {
    clearLiveTimer();
    const track = liveRef.current;
    liveRef.current = null;
    if (!track || track.done || track.skip) return;
    const patch = abortPatch(track, reason);
    if (patch) patchMessage(track.id, patch);
    else setMessages((prev) => prev.filter((m) => m.id !== track.id));
  }, [patchMessage, setMessages]);

  const armLive = useCallback(() => {
    clearLiveTimer();
    liveTimerRef.current = setTimeout(() => {
      liveTimerRef.current = null;
      closeLive(ABORT_INTERRUPTED);
    }, NARRATION_WAIT_MAX_MS);
  }, [closeLive]);

  const onDaNarrationPending = useCallback((payload) => {
    if (!enabled) return;
    if (payload?.message_id && liveRef.current?.id === payload.message_id) return;
    startWaiting();
  }, [enabled, startWaiting]);

  const onDaNarrationStart = useCallback((payload) => {
    if (!enabled || !payload?.message_id) return;
    stopWaiting();
    const live = liveRef.current;
    if (live && live.id === payload.message_id) return;
    if (live && !live.done) closeLive(ABORT_SUPERSEDED);
    const track = startTrack(payload);
    sourcesRef.current = Array.isArray(payload.sources) ? payload.sources : [];
    if (hasMessage(track.id)) {
      // Already on screen (replay block, or a second tab's replay): keep what is shown.
      liveRef.current = { ...track, skip: true, done: true };
      return;
    }
    liveRef.current = track;
    appendMessage(startMessage(payload, track));
    armLive();
  }, [enabled, stopWaiting, closeLive, hasMessage, appendMessage, armLive]);

  const onDaNarrationDelta = useCallback((payload) => {
    const track = liveRef.current;
    if (!enabled || !track || track.skip || payload?.message_id !== track.id) return;
    const { track: next, changed } = applyDelta(track, payload);
    liveRef.current = next;
    armLive();
    if (changed) patchMessage(track.id, { text: next.text, isStreaming: true, isDone: false });
  }, [enabled, patchMessage, armLive]);

  const onDaNarrationEnd = useCallback((payload) => {
    const track = liveRef.current;
    if (!enabled || !track || track.skip || payload?.message_id !== track.id || track.done) return;
    liveRef.current = { ...track, done: true };
    clearLiveTimer();
    stopWaiting();
    patchMessage(track.id, endPatch(track, payload, sourcesRef.current));
  }, [enabled, stopWaiting, patchMessage]);

  const onDaNarrationAbort = useCallback((payload) => {
    if (!enabled) return;
    // Also ends a pending (no start yet) narration's thinking row.
    stopWaiting();
    const track = liveRef.current;
    if (!track || payload?.message_id !== track.id) return;
    closeLive(payload?.reason);
  }, [enabled, stopWaiting, closeLive]);

  // Disconnect: a streaming bubble never stays open (whole sentences kept). A reconnect's replay repaints the
  // recorded text; replayed start/end for an id already on screen are ignored.
  const finalizeNarration = useCallback((reason = ABORT_INTERRUPTED) => {
    stopWaiting();
    closeLive(reason);
  }, [stopWaiting, closeLive]);

  // Session reset / clear: close any live bubble, then forget it (the transcript itself is reset by the caller).
  const resetNarration = useCallback(() => {
    closeLive(ABORT_INTERRUPTED);
    sourcesRef.current = [];
    stopWaiting();
  }, [closeLive, stopWaiting]);

  return {
    awaiting, stopWaiting, finalizeNarration, resetNarration,
    onDaNarrationPending, onDaNarrationStart, onDaNarrationDelta, onDaNarrationEnd, onDaNarrationAbort,
  };
}
